import { type ReactNode } from 'react'
import { Avatar, Group, Paper, SimpleGrid, Stack, Text } from '@mantine/core'
import { ColorPrimitives, SizePrimitives, Theme, type MaterialSymbolType, useTranslation } from '@front/model'
import { MaterialSymbolIcon } from '@components'
import { PreviewTrustList } from './PreviewTrustList'
import { PreviewSocialLinks } from './PreviewSocialLinks'
import { type CardPreviewProps } from './previewContent'

export const PreviewProfileColumn = ({ content, tokens }: CardPreviewProps): ReactNode => {
  const { t } = useTranslation()

  return (
    <Paper bg={ColorPrimitives.white['1000']} p='lg' radius='2xl' shadow='md'>
      <Stack gap='lg'>
        <Avatar
          bd={`4px solid ${Theme.light.background.neutral.secondary}`}
          bg={Theme.light.background.neutral.tertiary}
          c={Theme.light.icon.neutral.tertiary}
          radius='full'
          size={SizePrimitives['24']}
          src={content.repAvatarUrl}
        >
          <MaterialSymbolIcon icon='person' size={SizePrimitives['12']} />
        </Avatar>
        <Stack gap={0}>
          <Text c={tokens.ink} fw='bold' size='xl'>
            {t('Rep Name')}
          </Text>
          <Text c={tokens.accentTextOnLight} fw={600} size='sm'>
            {t('Sales Representative')}
          </Text>
          <Text c={Theme.light.text.neutral.tertiary} size='sm'>
            {content.companyName}
          </Text>
        </Stack>
        {content.showTrust && (
          <PreviewTrustList
            companyName={content.companyName}
            iconColor={tokens.accentOnLight}
            showRating={content.hasGoogleBusinessProfile}
            rating={content.rating}
            textColor={tokens.ink}
          />
        )}
        <Stack gap='xs'>
          <SimpleGrid cols={2} spacing='xs'>
            <ContactPill background={tokens.ink} icon='call' label={t('Call')} />
            <ContactPill background={tokens.ink} icon='chat' label={t('Text')} />
          </SimpleGrid>
          {content.showCta && (
            <Group
              bd={`1px solid ${ColorPrimitives.neutral['200']}`}
              bdrs='full'
              c={tokens.ink}
              gap='xs'
              justify='center'
              py='xs'
            >
              <MaterialSymbolIcon icon='edit' size='md' />
              <Text fw={600} size='sm'>
                {t('Leave a review')}
              </Text>
            </Group>
          )}
        </Stack>
        {content.showSocials && (
          <PreviewSocialLinks
            background={Theme.light.background.neutral.secondary}
            color={tokens.ink}
            links={content.socials}
          />
        )}
      </Stack>
    </Paper>
  )
}

type ContactPillProps = {
  background: string
  icon: MaterialSymbolType
  label: string
}

const ContactPill = ({ background, icon, label }: ContactPillProps): ReactNode => (
  <Group bg={background} bdrs='full' c={ColorPrimitives.white['1000']} gap='xs' justify='center' py='xs'>
    <MaterialSymbolIcon icon={icon} size='md' />
    <Text fw={600} size='sm'>
      {label}
    </Text>
  </Group>
)
